
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Edit, Trash2 } from "lucide-react";
import { calculateEndDate, calculateSequentialTaskDates } from "../utils/dateUtils";
import { ProjectTask } from "../types";
import { Checkbox } from "@/components/ui/checkbox";

interface CustomProjectDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateProject: (projectData: {
    name: string;
    description: string;
    projectLead: string;
    startDate: string;
    endDate: string;
    duration: number;
    tasks: ProjectTask[];
    excludeWeekends: boolean;
  }) => void;
  existingProjects?: { project_name: string }[];
}

interface CustomTask {
  id: string;
  name: string;
  duration: number;
}

export const CustomProjectDialog = ({ 
  isOpen, 
  onOpenChange, 
  onCreateProject, 
  existingProjects = [] 
}: CustomProjectDialogProps) => {
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [projectLead, setProjectLead] = useState("");
  const [startDate, setStartDate] = useState("");
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [customTasks, setCustomTasks] = useState<CustomTask[]>([]);
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskDuration, setNewTaskDuration] = useState(1);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editTaskName, setEditTaskName] = useState("");
  const [editTaskDuration, setEditTaskDuration] = useState(1);
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [isDuplicateTaskName, setIsDuplicateTaskName] = useState(false);
  const [duplicateTaskError, setDuplicateTaskError] = useState("");

  // Check for duplicate project name (case-insensitive)
  useEffect(() => {
    if (projectName.trim()) {
      const normalizedName = projectName.trim().toLowerCase();
      const isDuplicate = existingProjects.some(
        project => project.project_name.toLowerCase() === normalizedName
      );
      setIsDuplicateName(isDuplicate);
    } else {
      setIsDuplicateName(false);
    }
  }, [projectName, existingProjects]);

  // Check for duplicate task name when typing new task name
  useEffect(() => {
    if (newTaskName.trim()) {
      const normalizedTaskName = newTaskName.trim().toLowerCase();
      const isDuplicate = customTasks.some(
        task => task.name.toLowerCase() === normalizedTaskName
      );
      if (isDuplicate) {
        setIsDuplicateTaskName(true);
        setDuplicateTaskError("Task name already exists. Please enter a unique task name.");
      } else {
        setIsDuplicateTaskName(false);
        setDuplicateTaskError("");
      }
    } else {
      setIsDuplicateTaskName(false);
      setDuplicateTaskError("");
    }
  }, [newTaskName, customTasks]);

  // Check for duplicate task name when editing
  useEffect(() => {
    if (editTaskName.trim() && editingTaskId) {
      const normalizedTaskName = editTaskName.trim().toLowerCase();
      const isDuplicate = customTasks.some(
        task => task.id !== editingTaskId && task.name.toLowerCase() === normalizedTaskName
      );
      if (isDuplicate) {
        setDuplicateTaskError("Task name already exists. Please enter a unique task name.");
      } else {
        setDuplicateTaskError("");
      }
    } else {
      setDuplicateTaskError("");
    }
  }, [editTaskName, editingTaskId, customTasks]);

  // Calculate project end date and duration
  const totalDuration = customTasks.reduce((sum, task) => sum + task.duration, 0);
  const endDate = startDate ? calculateEndDate(startDate, totalDuration, excludeWeekends) : "";

  // Clear all fields when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      clearAllFields();
    }
  }, [isOpen]);

  // Recalculate task dates whenever tasks change or start date changes
  useEffect(() => {
    if (startDate && customTasks.length > 0) {
      const taskDurations = customTasks.map(task => ({ duration: task.duration }));
      const { startDates, endDates } = calculateSequentialTaskDates(startDate, taskDurations, excludeWeekends);
      console.log("Recalculated task dates:", { startDates, endDates });
    }
  }, [customTasks, startDate, excludeWeekends]);

  const clearAllFields = () => {
    setProjectName("");
    setProjectDescription("");
    setProjectLead("");
    setStartDate("");
    setExcludeWeekends(true);
    setCustomTasks([]);
    setNewTaskName("");
    setNewTaskDuration(1);
    setEditingTaskId(null);
    setEditTaskName("");
    setEditTaskDuration(1);
    setIsDuplicateName(false);
    setIsDuplicateTaskName(false);
    setDuplicateTaskError("");
  };

  const handleStartOver = () => {
    clearAllFields();
  };

  const handleAddTask = () => {
    if (!newTaskName.trim() || isDuplicateTaskName) return;
    
    const newTask: CustomTask = {
      id: crypto.randomUUID(),
      name: newTaskName.trim(),
      duration: newTaskDuration
    };
    
    setCustomTasks([...customTasks, newTask]);
    setNewTaskName("");
    setNewTaskDuration(1);
    setIsDuplicateTaskName(false);
    setDuplicateTaskError("");
  };

  const handleEditTask = (taskId: string) => {
    const task = customTasks.find(t => t.id === taskId);
    if (task) {
      setEditingTaskId(taskId);
      setEditTaskName(task.name);
      setEditTaskDuration(task.duration);
    }
  };

  const handleSaveEdit = () => {
    if (!editTaskName.trim() || !editingTaskId) return;
    
    // Check for duplicates before saving
    const normalizedTaskName = editTaskName.trim().toLowerCase();
    const isDuplicate = customTasks.some(
      task => task.id !== editingTaskId && task.name.toLowerCase() === normalizedTaskName
    );
    
    if (isDuplicate) {
      return; // Don't save if duplicate
    }
    
    setCustomTasks(customTasks.map(task => 
      task.id === editingTaskId 
        ? { ...task, name: editTaskName.trim(), duration: editTaskDuration }
        : task
    ));
    
    setEditingTaskId(null);
    setEditTaskName("");
    setEditTaskDuration(1);
    setDuplicateTaskError("");
  };

  const handleDeleteTask = (taskId: string) => {
    setCustomTasks(customTasks.filter(task => task.id !== taskId));
    // Clear any duplicate error when deleting a task
    setDuplicateTaskError("");
  };

  const handleCreateProject = async () => {
    if (!projectName.trim() || !startDate || customTasks.length === 0 || isDuplicateName) return;

    // Convert custom tasks to ProjectTask format with calculated dates
    const taskDurations = customTasks.map(task => ({ duration: task.duration }));
    const { startDates, endDates } = calculateSequentialTaskDates(startDate, taskDurations, excludeWeekends);
    
    const projectTasks: ProjectTask[] = customTasks.map((task, index) => ({
      taskId: `T${String(index + 1).padStart(3, '0')}`,
      name: task.name,
      duration: task.duration,
      startDate: startDates[index] || "",
      endDate: endDates[index] || ""
    }));

    await onCreateProject({
      name: projectName,
      description: projectDescription,
      projectLead: projectLead,
      startDate,
      endDate,
      duration: totalDuration,
      tasks: projectTasks,
      excludeWeekends
    });

    // Dialog will be closed by the parent component after successful project creation
    clearAllFields();
  };

  const isCreateButtonDisabled = !projectName.trim() || !startDate || customTasks.length === 0 || isDuplicateName;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto text-black bg-white border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-black text-xl">Add Custom Project</DialogTitle>
        </DialogHeader>
        
        <div className="flex gap-3">
          {/* Left side - Project details */}
          <div className="flex-1 space-y-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-bold text-black">Project Name</label>
                {isDuplicateName && (
                  <span className="text-red-500 text-sm">
                    Duplicate name detected. Please enter a unique name.
                  </span>
                )}
              </div>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                className="bg-white border-primary/20 text-gray h-8"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-black mb-1">Project Description</label>
              <Textarea
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder="Enter project description"
                className="bg-white border-primary/20 text-gray min-h-[80px] py-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-black mb-1">Project Lead's Email</label>
              <Input
                value={projectLead}
                onChange={(e) => setProjectLead(e.target.value)}
                placeholder="Enter project lead's email"
                className="bg-white border-primary/20 text-gray h-8"
              />
            </div>
            
            {/* 3-column grid for Start Date, End Date, Duration */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-sm font-bold text-black mb-1">Start Date</label>
              </div>
              <div>
                <label className="block text-sm font-bold text-black mb-1">End Date</label>
              </div>
              <div>
                <label className="block text-sm font-bold text-black mb-1">Duration (Days)</label>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-white border-primary/20 text-gray h-8"
                />
              </div>
              <div>
                <Input
                  value={endDate}
                  readOnly
                  className="bg-gray-100 border-gray-300 text-black h-8"
                />
              </div>
              <div>
                <Input
                  value={totalDuration}
                  readOnly
                  className="bg-gray-100 border-gray-300 text-black h-8"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="excludeWeekends"
                checked={excludeWeekends}
                onCheckedChange={(checked) => setExcludeWeekends(checked as boolean)}
              />
              <label htmlFor="excludeWeekends" className="text-sm text-black">
                Exclude weekends when projecting date and duration
              </label>
            </div>
          </div>
          
          {/* Right side - Project tasks */}
          <div className="flex-1 space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-black">Project Tasks</h3>
              <h3 className="text-sm font-bold text-black">Estimated Days</h3>
            </div>
            
            {/* Add new task form */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Input
                  value={newTaskName}
                  onChange={(e) => setNewTaskName(e.target.value)}
                  placeholder="Enter task name"
                  className={`bg-white border-primary/20 text-black h-8 ${isDuplicateTaskName ? 'border-red-500' : ''}`}
                />
                {duplicateTaskError && !editingTaskId && (
                  <p className="text-red-500 text-xs mt-1">{duplicateTaskError}</p>
                )}
              </div>
              <div className="w-16">
                <Input
                  type="number"
                  min="1"
                  value={newTaskDuration}
                  onChange={(e) => setNewTaskDuration(parseInt(e.target.value) || 1)}
                  className="bg-white border-primary/20 text-black h-8"
                />
              </div>
              <Button
                onClick={handleAddTask}
                size="icon"
                disabled={isDuplicateTaskName || !newTaskName.trim() || newTaskDuration < 1}
                className="bg-primary hover:bg-primary/90 h-8 w-8 disabled:opacity-50"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            
            {/* Tasks table with scroll area */}
            <div className="border border-primary/20 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-black font-bold w-[50%] text-left">Task Name</TableHead>
                    <TableHead className="text-black font-bold w-[30%] text-left">Duration (Days)</TableHead>
                    <TableHead className="text-black font-bold w-[20%] text-left">Actions</TableHead>
                  </TableRow>
                </TableHeader>
              </Table>
              <ScrollArea className="h-48">
                <Table>
                  <TableBody>
                    {customTasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell className="w-[50%] text-left">
                          {editingTaskId === task.id ? (
                            <div>
                              <Input
                                value={editTaskName}
                                onChange={(e) => setEditTaskName(e.target.value)}
                                className={`bg-white border-primary/20 text-black h-7 ${duplicateTaskError && editingTaskId === task.id ? 'border-red-500' : ''}`}
                              />
                              {duplicateTaskError && editingTaskId === task.id && (
                                <p className="text-red-500 text-xs mt-1">{duplicateTaskError}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-black text-left">{task.name}</span>
                          )}
                        </TableCell>
                        <TableCell className="w-[30%] text-left">
                          {editingTaskId === task.id ? (
                            <Input
                              type="number"
                              min="1"
                              value={editTaskDuration}
                              onChange={(e) => setEditTaskDuration(parseInt(e.target.value) || 1)}
                              className="bg-white border-primary/20 text-black w-14 h-7"
                            />
                          ) : (
                            <span className="text-black text-left">{task.duration}</span>
                          )}
                        </TableCell>
                        <TableCell className="w-[20%] text-left">
                          <div className="flex gap-1">
                            {editingTaskId === task.id ? (
                              <Button
                                onClick={handleSaveEdit}
                                size="sm"
                                disabled={duplicateTaskError !== "" || !editTaskName.trim() || editTaskDuration < 1}
                                className="bg-primary hover:bg-primary/90 h-6 px-2 text-xs disabled:opacity-50"
                              >
                                Save
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleEditTask(task.id)}
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                              >
                                <Edit className="h-3 w-3 text-primary" />
                              </Button>
                            )}
                            <Button
                              onClick={() => handleDeleteTask(task.id)}
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {customTasks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-gray-400 py-6">
                          No tasks added yet. Add your first task above.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </div>
        </div>
        
        {/* Footer buttons */}
        <div className="flex justify-between items-center mt-4">
          <Button
            onClick={handleStartOver}
            variant="outline"
            className="bg-white text-black border-gray-300 hover:bg-gray-50 h-8 px-3"
          >
            Start over
          </Button>
          <div className="flex gap-3">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="bg-white text-black border-gray-300 hover:bg-gray-50 h-8 px-3"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={isCreateButtonDisabled}
              className="bg-primary text-black hover:bg-primary/90 h-8 px-3"
            >
              Create custom project
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
